import { AVAILABILITY_SLOTS as SHARED_AVAILABILITY_SLOTS } from '@aslgo/shared';
import type { SmsConsentChoice } from './SmsConsent.js';

export const AGE_RANGES = ['14-17', '18-24', '25-44', '45+'] as const;
export type AgeRange = typeof AGE_RANGES[number];

export const LEARNING_TIMES = [
  { id: 'morning', label: 'Morning' },
  { id: 'afternoon', label: 'Afternoon' },
  { id: 'evening', label: 'Evening' },
  { id: 'anytime', label: 'Anytime' },
] as const;

export const LEARNER_AGE_OPTIONS = [
  { id: '14-17', label: '14–17' },
  { id: '18-24', label: '18–24' },
  { id: '25-44', label: '25–44' },
  { id: '45+',   label: '45 or older' },
] as const;

export const HEARD_ABOUT_OPTIONS = [
  { id: 'facebook', label: 'Facebook' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'friends_family', label: 'Friends & Family' },
  { id: 'other', label: 'Other' },
] as const;

export const IMMERSION_REASONS = [
  { id: 'interpreting_student', label: 'Interpreting student' },
  { id: 'parent_of_deaf_child', label: 'Parent of a Deaf child' },
  { id: 'deaf_learning_asl', label: 'Deaf person learning ASL' },
  { id: 'just_for_fun', label: 'Just for fun' },
  { id: 'other', label: 'Other' },
] as const;

export const INTERPRETER_FIELDS = [
  { id: 'education', label: 'Education' },
  { id: 'legal', label: 'Legal' },
  { id: 'medical', label: 'Medical' },
  { id: 'theatre_concerts', label: 'Theatre & Concerts' },
  { id: 'other', label: 'Other' },
] as const;

export const EXPERTISE_AREAS = [
  { id: 'Educational', label: 'Educational' },
  { id: 'Legal', label: 'Legal' },
  { id: 'Medical', label: 'Medical' },
  { id: 'Science', label: 'Science' },
  { id: 'Performing Arts', label: 'Performing Arts' },
  { id: 'Technology', label: 'Technology' },
  { id: 'other', label: 'Other' },
] as const;

export const GUIDE_AGE_OPTIONS = [
  { id: '18-24', label: '18–24' },
  { id: '25-44', label: '25–44' },
  { id: '45-64', label: '45–64' },
  { id: '65+',   label: '65+' },
] as const;

export const ASL_YEARS_OPTIONS = [
  { id: '0-5', label: '0–5' },
  { id: '6-10', label: '6–10' },
  { id: '11-20', label: '11–20' },
  { id: '21+', label: '21+' },
] as const;

export const INTERESTED_CDI_OPTIONS = [
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
  { id: 'not_sure', label: 'Not Sure' },
] as const;

// Canonical list now lives in packages/shared (mirrored server-side by the
// backend profile route). Re-exported under this name so existing importers
// (guideSteps.tsx, ProfileView.tsx) keep working unchanged.
export const AVAILABILITY_SLOTS = SHARED_AVAILABILITY_SLOTS;

/** 50 states + DC (alphabetical), then the five U.S. territories. `id` is the
 *  two-letter code that gets stored; `label` is what the dropdown displays. */
export const US_STATES = [
  { id: 'AL', label: 'Alabama' },
  { id: 'AK', label: 'Alaska' },
  { id: 'AZ', label: 'Arizona' },
  { id: 'AR', label: 'Arkansas' },
  { id: 'CA', label: 'California' },
  { id: 'CO', label: 'Colorado' },
  { id: 'CT', label: 'Connecticut' },
  { id: 'DE', label: 'Delaware' },
  { id: 'DC', label: 'District of Columbia' },
  { id: 'FL', label: 'Florida' },
  { id: 'GA', label: 'Georgia' },
  { id: 'HI', label: 'Hawaii' },
  { id: 'ID', label: 'Idaho' },
  { id: 'IL', label: 'Illinois' },
  { id: 'IN', label: 'Indiana' },
  { id: 'IA', label: 'Iowa' },
  { id: 'KS', label: 'Kansas' },
  { id: 'KY', label: 'Kentucky' },
  { id: 'LA', label: 'Louisiana' },
  { id: 'ME', label: 'Maine' },
  { id: 'MD', label: 'Maryland' },
  { id: 'MA', label: 'Massachusetts' },
  { id: 'MI', label: 'Michigan' },
  { id: 'MN', label: 'Minnesota' },
  { id: 'MS', label: 'Mississippi' },
  { id: 'MO', label: 'Missouri' },
  { id: 'MT', label: 'Montana' },
  { id: 'NE', label: 'Nebraska' },
  { id: 'NV', label: 'Nevada' },
  { id: 'NH', label: 'New Hampshire' },
  { id: 'NJ', label: 'New Jersey' },
  { id: 'NM', label: 'New Mexico' },
  { id: 'NY', label: 'New York' },
  { id: 'NC', label: 'North Carolina' },
  { id: 'ND', label: 'North Dakota' },
  { id: 'OH', label: 'Ohio' },
  { id: 'OK', label: 'Oklahoma' },
  { id: 'OR', label: 'Oregon' },
  { id: 'PA', label: 'Pennsylvania' },
  { id: 'RI', label: 'Rhode Island' },
  { id: 'SC', label: 'South Carolina' },
  { id: 'SD', label: 'South Dakota' },
  { id: 'TN', label: 'Tennessee' },
  { id: 'TX', label: 'Texas' },
  { id: 'UT', label: 'Utah' },
  { id: 'VT', label: 'Vermont' },
  { id: 'VA', label: 'Virginia' },
  { id: 'WA', label: 'Washington' },
  { id: 'WV', label: 'West Virginia' },
  { id: 'WI', label: 'Wisconsin' },
  { id: 'WY', label: 'Wyoming' },
  { id: 'AS', label: 'American Samoa' },
  { id: 'GU', label: 'Guam' },
  { id: 'MP', label: 'Northern Mariana Islands' },
  { id: 'PR', label: 'Puerto Rico' },
  { id: 'VI', label: 'U.S. Virgin Islands' },
] as const;

/** Flat answer bag for both flows. Credential fields are filled on page 1;
 *  everything else is collected progressively, hence optional. */
export interface RegFormData {
  // credentials (page 1, both flows)
  email: string;
  password: string;
  displayName: string;
  fullName: string;
  ageRange: string;
  // learner — basic info
  phone?: string;
  city?: string;
  state?: string;
  ethnicity?: string;
  heardAbout?: string;
  // learner — goals & availability
  preferredTimes?: string[];
  motivation?: string;
  goal?: string;
  // learner — plan & agreement
  planChoice?: string;
  agreementAccepted?: boolean;
  agreementSignature?: string;   // placeholder e-signature
  paymentPlaceholder?: string;   // placeholder payment-setup field
  // learner — additional profile
  heardAboutOther?: string;
  immersionReasons?: string[];
  immersionReasonOther?: string;
  wantsInterpreter?: boolean;
  interpreterFields?: string[];
  interpreterFieldOther?: string;
  // minor (14-17) guardian + consent
  guardianName?: string;
  guardianRelationship?: string;
  guardianEmail?: string;
  guardianPhone?: string;
  guardianMailingAddress?: string;
  guardianConsents?: Record<string, boolean>;
  guardianSignature?: string;    // placeholder
  minorSignature?: string;       // optional placeholder
  // guide
  aslBackground?: string;
  professionalBackground?: string;
  availability?: string;
  introVideoRef?: string;        // placeholder text
  bgCheckConsent?: boolean;
  govIdRef?: string;             // placeholder text
  resumeRef?: string;            // placeholder text
  achPlaceholder?: string;       // placeholder text
  digitalSignature?: string;     // placeholder
  // guide — v2 profile
  aslYears?: string;
  isCdi?: boolean;
  interestedInCdi?: string;
  expertiseAreas?: string[];
  expertiseAreaOther?: string;
  availabilitySlots?: string[];
  codeOfConductConsent?: boolean;
  signatureAgree?: boolean;
  /** Twilio SMS consent, asked once a mobile number is entered. Undefined
   *  until the guide answers; 'opt_out' is a real answer, not a missing one. */
  smsConsent?: SmsConsentChoice;
}

export function emptyRegFormData(): RegFormData {
  return { email: '', password: '', displayName: '', fullName: '', ageRange: '' };
}
